import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  MdKeyboardDoubleArrowLeft,
  MdKeyboardDoubleArrowRight
} from 'react-icons/md'
import { useSearchParams } from 'react-router-dom'
import { notification, Pagination, Typography } from 'antd'
import Const from 'constants/constant'
import TableColumn from 'constants/tableColumn'
import dayjs from 'dayjs'
import useAuth from 'hooks/useAuth'
import useDirectory from 'hooks/useDirectory'
import { ListBuilding } from 'models/Building'
import { Role } from 'models/User'
import DeleleFloorModal from 'pages/building/listBuilding/components/DeleleFloorModal'
import EditMenu from 'pages/building/listBuilding/components/EditMenu'
import useSWR from 'swr'
import { OrderByType } from 'types'
import { BuildingCopyStatus, BuildingStatus } from 'types/building'

import LazyThumbnail from 'components/atoms/LazyThumbnail'
import ShootingStatusBadge from 'components/atoms/ShootingStatusBadge'
import EllipsisIcon from 'components/icons/EllipsisIcon'
import NoImageIcon from 'components/icons/NoImageIcon'
import ExtraTable from 'components/organisms/ExtraTable'

import DetailCopyModal from './DetailCopyModal'
import HeaderListBuilding from './HeaderListBuilding'

type Props = {
  isFormSearch?: boolean
  idUser?: string
}

enum EditMenuAction {
  COPY_PREVIEW_LINK = 'copy',
  DUPLICATESAME = 'duplicateSame',
  BCP = '20',
  TOUR = '10',
  BUILDING360 = '0',
  DELETE = 'delete'
}

export default function TableBuilding({ isFormSearch, idUser }: Props) {
  const { t } = useTranslation()
  const { profile } = useAuth()
  const defaultField = 'updatedAt'

  const [queries, setQueries] = useSearchParams()
  const page = queries.get('page')
  const { getBuiildingType, getDirectoryBasename } = useDirectory()

  // const [, setMenuTargetId] = useState<number | string | null>(null)
  const [isShowModalDeleteBuilding, setIsShowModalDeleteBuilding] =
    useState(false)
  const [isShowDetailCopyModal, setIsShowDetailCopyModal] = useState(false)
  const [currentCopyBuildingType, setCurrentCopyBuildingType] = useState(0)
  const parentDivRef = useRef<HTMLDivElement>(null)
  const [menuPosition, setMenuPosition] = useState<{
    top: number
    left: number
  } | null>(null)

  const [currentBuilding, setCurrentBuilding] = useState<{
    id: number
    name: string
  }>({ id: -1, name: '' })

  const params = useMemo(() => {
    sessionStorage.setItem('buildingParams', queries.toString())
    const convertedQueries = Object.fromEntries(queries.entries())
    return {
      ...convertedQueries,
      page: page || 1,
      pageSize: 10,
      id: idUser === 'me' ? `${profile?.id}` : idUser,
      sort: convertedQueries?.sort || defaultField,
      buildingType: getBuiildingType(),
      sortOrder: convertedQueries?.sortOrder
        ? convertedQueries.sortOrder
        : OrderByType.descend
    }
  }, [getBuiildingType, idUser, page, profile?.id, queries])

  const {
    data: buildings,
    isValidating,
    mutate
  } = useSWR<ListBuilding>({
    url: `/v1/buildings`,
    params
  })

  // copy building

  const hiddenPagination = useMemo(
    () =>
      Boolean(
        buildings?.pagination.total &&
          buildings.pagination.total /
            Const.DEFAULT_PAGINATION.defaultPageSize >
            1
      ),
    [buildings?.pagination.total]
  )

  const handleCopyPreviewUrl = (buildingId: number) => {
    const link = `${
      window.location.origin
    }${getDirectoryBasename()}/viewer/buildings/${buildingId}`

    navigator.clipboard.writeText(link)
    notification.success({
      key: 'copy-successful',
      message: t('translation.copyUrlSuccessful')
    })
  }

  const getCopyStatusBadge = (status: BuildingCopyStatus) => {
    const styles = {
      [BuildingCopyStatus.PENDING]: {
        bg: 'bg-yellow-50 border border-yellow-200',
        text: 'text-yellow-700',
        label: t('building.pending')
      },
      [BuildingCopyStatus.PROCESSING]: {
        bg: 'bg-blue-50 border border-blue-200',
        text: 'text-blue-700',
        label: t('building.copying')
      },
      [BuildingCopyStatus.FAILED]: {
        bg: 'bg-red-50 border border-red-200',
        text: 'text-red-700',
        label: t('building.copyFailed')
      }
    }

    const style = styles[status as keyof typeof styles]
    if (!style) return null

    return (
      <div
        className={`inline-flex items-center justify-center px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${style.bg} ${style.text}`}
      >
        {style.label}
      </div>
    )
  }

  /**
   * Generates a list of menu options for a building based on its type.
   *
   * The menu includes actions such as copying the preview link, duplicating the building,
   * accessing different versions, and deleting the building. The options vary depending
   * on the building type, which is determined by `getBuiildingType()`:
   *
   * - For type 10: Includes options for "360 Building" and "BCP".
   * - For type 20: Includes options for "360 Building" and "Tour".
   * - For other types: Includes options for "Tour" and "BCP".
   *
   * @returns {Array} An array of menu option objects, each containing a `label`, `key`,
   *          and optionally a `color` for the delete action or a `subMenu` for
   *          additional options.
   */

  const getMenuOptions = () => {
    let subMenu: { label: string; key: string }[] = []

    switch (getBuiildingType()) {
      case 10:
        subMenu = [
          { label: t('building.building360'), key: EditMenuAction.BUILDING360 },
          { label: t('building.bcp'), key: EditMenuAction.BCP }
        ]
        break
      case 20:
        subMenu = [
          { label: t('building.building360'), key: EditMenuAction.BUILDING360 },
          { label: t('building.tour'), key: EditMenuAction.TOUR }
        ]
        break
      default:
        subMenu = [
          { label: t('building.tour'), key: EditMenuAction.TOUR },
          { label: t('building.bcp'), key: EditMenuAction.BCP }
        ]
        break
    }

    return [
      { label: t('building.copy'), key: EditMenuAction.COPY_PREVIEW_LINK },
      { label: t('building.duplicateSame'), key: EditMenuAction.DUPLICATESAME },
      {
        label: t('building.other-version'),
        key: '',
        subMenu
      },
      { label: t('building.delete'), key: EditMenuAction.DELETE, color: 'red' }
    ]
  }

  const listBuilding = buildings?.edges?.map((item) => {
    const fullNameUser = `${item.latestEditedBy?.lastName || ''} ${
      item.latestEditedBy?.firstName || ''
    }`
    const buildingStatus = () => {
      if (item.status === 20 && getBuiildingType() === 10) {
        return 'building.tourStatusPrivate'
      }
      switch (item.status) {
        default:
          return `building.${item.status}`
      }
    }

    const renderBuildingStatus = () => {
      if (!item.status) return ''
      if (item.status === BuildingStatus.IMAGE) {
        return (
          <ShootingStatusBadge
            buildingId={item.id}
            statusText={t(buildingStatus())}
          />
        )
      }
      return t(buildingStatus())
    }

    return {
      ...item,
      buildingStatus: renderBuildingStatus(),
      branchName: item.branch ? (
        <div>
          <Typography>
            {item.branch.name !== 'その他' ? item.branch.name : ''}
          </Typography>
          <Typography>
            {item.branch.serviceStoreName !== 'その他'
              ? item.branch.serviceStoreName
              : ''}
          </Typography>
        </div>
      ) : (
        ''
      ),
      orgName: (
        <div className="grid grid-cols-1">
          <Typography.Paragraph
            className="!mb-0"
            ellipsis={{
              rows: 2,
              tooltip: item.organizationName
            }}
          >
            {item.organizationName}
          </Typography.Paragraph>
        </div>
      ),
      name: (
        <div className="grid grid-cols-1">
          <Typography.Paragraph
            className="!mb-0"
            ellipsis={{
              rows: 2,
              tooltip: item.name
            }}
          >
            {item.name}
          </Typography.Paragraph>
        </div>
      ),
      updatedAt: (
        <div className="grid grid-cols-1">
          <Typography.Paragraph className="!mb-0">
            {dayjs(item.latestUpdated).format('YYYY/MM/DD HH:mm')}
          </Typography.Paragraph>
          <Typography.Paragraph
            ellipsis={{
              rows: 1,
              tooltip: fullNameUser
            }}
            className="!mb-0 truncate"
          >
            {fullNameUser}
          </Typography.Paragraph>
        </div>
      ),
      image: item.s3Path ? (
        <LazyThumbnail
          name="thumbnail"
          url={item.thumbnail || item.s3Path}
          className="w-[120px] h-[67.5px] !bg-[#021120]"
          width={120}
          height={67.5}
        />
      ) : (
        <NoImageIcon width={120} height={67.5} />
      ),
      createdAt: (
        <div className="grid grid-cols-1">
          <Typography.Paragraph className="!mb-0">
            {dayjs(item.createdAt).format('YYYY/MM/DD')}
          </Typography.Paragraph>
        </div>
      ),

      edit: (() => {
        if (profile?.role !== Role.SA && profile?.role !== Role.BA) return

        if (
          item.copyStatus === BuildingCopyStatus.PENDING ||
          item.copyStatus === BuildingCopyStatus.PROCESSING ||
          item.copyStatus === BuildingCopyStatus.FAILED
        ) {
          return getCopyStatusBadge(item.copyStatus)
        }
        if (!item.deletedAt) {
          const isDisabled =
            item.copyStatus !== BuildingCopyStatus.BUILDING_SOURCE &&
            item.copyStatus !== BuildingCopyStatus.SUCCESS
          return (
            <div
              className={`edit-column w-10 h-10 flex items-center justify-end rounded-md transition-colors text-black ${
                isDisabled ? 'opacity-0 pointer-events-none' : 'cursor-pointer'
              }`}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                const rect = (
                  e.currentTarget as HTMLElement
                ).getBoundingClientRect()

                // Calculate menu dimensions (assuming menu height is around 200px)
                const menuHeight = 170
                const menuWidth = 170

                // Check if menu would be displayed below the viewport
                const wouldBeBelowViewport =
                  rect.bottom + menuHeight > window.innerHeight

                // Check if menu would be displayed to the right of the viewport
                const wouldBeRightOfViewport =
                  rect.right > window.innerWidth - menuWidth

                setMenuPosition({
                  top: wouldBeBelowViewport
                    ? rect.top + window.scrollY - menuHeight
                    : rect.bottom + window.scrollY,
                  left: wouldBeRightOfViewport
                    ? rect.left - menuWidth
                    : rect.right - menuWidth
                })

                setCurrentBuilding({
                  id: item.id,
                  name: item.name
                })
              }}
            >
              <EllipsisIcon className="w-4 h-4 hover:opacity-50" />
            </div>
          )
        }
        return null
      })()
    }
  })

  /**
   * Handles different edit actions for a building based on the provided option.
   *
   * @param {EditMenuAction} option - The action option to execute. It can be one of the following:
   *   - EditMenuAction.DELETE: Shows a modal to confirm building deletion.
   *   - EditMenuAction.COPY_PREVIEW_LINK: Copies the preview URL of the current building to the clipboard.
   *   - EditMenuAction.DUPLICATESAME: Opens a modal for copying the building within the same version.
   *   - Other values: Opens a modal for copying the building to another version, setting the target version type.
   */
  const handleEditBuilding = (option: EditMenuAction) => {
    if (option === EditMenuAction.DELETE) {
      setIsShowModalDeleteBuilding(true)
    } else if (option === EditMenuAction.COPY_PREVIEW_LINK) {
      handleCopyPreviewUrl(currentBuilding.id)
    } else if (option === EditMenuAction.DUPLICATESAME) {
      //  copy in the same building version
      setIsShowDetailCopyModal(true)
      setCurrentCopyBuildingType(getBuiildingType())
    } else {
      // copy to other version
      setIsShowDetailCopyModal(true)
      setCurrentCopyBuildingType(Number(option))
    }
  }

  const onTableChange = useCallback(
    (sorter: string, field: string) => {
      if (sorter) {
        queries.set('sort', field)
        queries.set(
          'sortOrder',
          OrderByType[sorter as keyof typeof OrderByType]
        )
      } else {
        queries.delete('sort')
        queries.delete('sortOrder')
      }
      setQueries(queries)
    },
    [queries, setQueries]
  )

  const handleSearch = (value: any) => {
    Object.keys(value).forEach((key) => {
      if (queries.get(key) && !value[key]) {
        queries.delete(key)
      }
      value[key] && queries.set(key, value[key])
    })
    queries.set('page', '1')
    setQueries(queries)
  }

  const onPageChange = useCallback(
    (page: number) => {
      queries.set('page', `${page}`)
      setQueries(queries)
    },
    [queries, setQueries]
  )

  // Effect  Hide menu when scrolling
  useEffect(() => {
    const container = parentDivRef.current
    if (container) {
      container.addEventListener('scroll', () => {
        if (menuPosition) setMenuPosition(null)
      })
    }
  }, [menuPosition])

  const itemRender = (_: any, type: string, originalElement: any) => {
    if (type === 'prev') {
      return (
        <div className="icon-prev flex items-center justify-center">
          <MdKeyboardDoubleArrowLeft className="theme-text-primary" />
        </div>
      )
    }
    if (type === 'next') {
      return (
        <div className="icon-next flex items-center justify-center">
          <MdKeyboardDoubleArrowRight className="theme-text-primary" />
        </div>
      )
    }
    return originalElement
  }

  return (
    <div ref={parentDivRef} style={{ overflow: 'auto', height: '100%' }}>
      {isFormSearch && (
        <HeaderListBuilding
          handleSearch={handleSearch}
          getNewBuilding={mutate}
        />
      )}

      <ExtraTable
        isLoading={isValidating && !buildings}
        columns={TableColumn.COLUMN_BUILDING}
        data={listBuilding}
        onChange={onTableChange}
        total={buildings?.pagination.total}
        role={profile?.role}
        refetch={mutate}
      />

      {hiddenPagination ? (
        <div className="flex justify-center">
          <Pagination
            showSizeChanger={false}
            className="my-4 custom-pagination"
            current={+(page || 1)}
            pageSize={10}
            total={buildings?.pagination.total || 0}
            onChange={onPageChange}
            itemRender={itemRender}
          />
        </div>
      ) : null}

      {menuPosition && (
        <EditMenu
          options={getMenuOptions()}
          position={menuPosition}
          onClose={() => setMenuPosition(null)}
          onSelect={(option: any) => handleEditBuilding(option)}
        />
      )}

      {isShowModalDeleteBuilding && (
        <DeleleFloorModal
          type="building"
          handleCancel={() => {
            setIsShowModalDeleteBuilding(false)
            setCurrentBuilding({ id: 0, name: '' })
          }}
          buildingId={currentBuilding.id}
          buildingName={currentBuilding.name}
          handleReload={mutate}
        />
      )}

      {isShowDetailCopyModal && (
        <DetailCopyModal
          handleCancel={() => {
            setIsShowDetailCopyModal(false)
          }}
          buildingId={currentBuilding.id}
          handleReload={mutate}
          buildingType={currentCopyBuildingType}
        />
      )}
    </div>
  )
}

TableBuilding.defaultProps = {
  isFormSearch: true,
  idUser: undefined
}
